import { FC, useMemo, useState } from 'react';
import { useField } from 'informed';
import css from './styles.module.scss';
import { SearchField, Button } from '@components';
import { ArrowLeftIcon, ArrowRightIcon } from '@assets/svg';
import { IDropdownOption, InformedFieldState } from '@common/interfaces';

type Option = {
  label: string;
  value: string;
};

interface IDualListField {
  name: string;
  leftTitle: string;
  rightTitle: string;
  options: Option[];
}

const DualListField: FC<IDualListField> = (props) => {
  const { leftTitle, rightTitle, options } = props;
  const { fieldState, fieldApi, render } = useField(props);

  const { value: selected = [], error, showError } = fieldState as InformedFieldState<IDropdownOption[]>;
  const { setValue, setError } = fieldApi;

  const [leftFilter, setLeftFilter] = useState('');
  const [rightFilter, setRightFilter] = useState('');

  const [activeLeft, setActiveLeft] = useState<Set<string>>(new Set());
  const [activeRight, setActiveRight] = useState<Set<string>>(new Set());

  const selectedValues = useMemo(() => new Set(selected.map((o) => o.value)), [selected]);

  const available = useMemo(() => options.filter((o) => !selectedValues.has(o.value)), [options, selectedValues]);

  const filteredAvailable = available.filter((o) => o.label.toLowerCase().includes(leftFilter.toLowerCase()));

  const filteredSelected = selected.filter((o) => o.label.toLowerCase().includes(rightFilter.toLowerCase()));

  const toggleLeft = (value: string) => {
    const next = new Set(activeLeft);
    next.has(value) ? next.delete(value) : next.add(value);
    setActiveLeft(next);
    setError(undefined);
  };

  const toggleRight = (value: string) => {
    const next = new Set(activeRight);
    next.has(value) ? next.delete(value) : next.add(value);
    setActiveRight(next);
    setError(undefined);
  };

  const addSelected = () => {
    const toAdd = available.filter((o) => activeLeft.has(o.value));
    if (!toAdd.length) return;
    setValue([...selected, ...toAdd]);
    setActiveLeft(new Set());
    setError(undefined);
  };

  const removeSelected = () => {
    if (!activeRight.size) return;
    setValue(selected.filter((o) => !activeRight.has(o.value)));
    setActiveRight(new Set());
    setError(undefined);
  };

  const addAll = () => {
    setValue([...selected, ...available]);
    setActiveLeft(new Set());
  };

  const removeAll = () => {
    setValue([]);
    setActiveRight(new Set());
  };

  return render(
    <div className={css.wrapper}>
      <div className={css.column}>
        <div className={css.label}>{leftTitle}</div>
        <SearchField placeholder='Filter' onChange={setLeftFilter} value={leftFilter} />
        <div className={css.list}>
          {filteredAvailable.map((o, idx) => (
            <div
              key={idx}
              className={`${css.item} ${activeLeft.has(o.value) ? css.active : ''}`}
              onClick={() => toggleLeft(o.value)}
            >
              {o.label}
            </div>
          ))}
        </div>
        <Button
          text='Choose all'
          iconR={<ArrowRightIcon />}
          className={css.chooseLink}
          variant='text'
          onClick={addAll}
        />
      </div>
      <div className={css.controls}>
        <Button
          iconL={<ArrowRightIcon />}
          variant='lynch'
          className={css.button}
          onClick={addSelected}
          disabled={!activeLeft.size}
        />
        <Button
          iconL={<ArrowLeftIcon />}
          variant='lynch'
          className={css.button}
          onClick={removeSelected}
          disabled={!activeRight.size}
        />
      </div>
      <div className={css.column}>
        <div className={css.label}>{rightTitle}</div>
        <SearchField placeholder='Filter' value={rightFilter} onChange={setRightFilter} />
        <div className={css.list}>
          {filteredSelected.map((o, idx) => (
            <div
              key={idx}
              className={`${css.item} ${activeRight.has(o.value) ? css.active : ''}`}
              onClick={() => toggleRight(o.value)}
            >
              {o.label}
            </div>
          ))}
        </div>
        <Button
          text='Remove all'
          iconL={<ArrowLeftIcon />}
          className={css.removeLink}
          variant='text'
          onClick={removeAll}
        />
      </div>
      {showError ? <div className={css.error}>{error}</div> : null}
    </div>
  );
};

export default DualListField;
